import { useLayoutEffect, useRef } from 'react';
import { UserCog, LogOut } from 'lucide-react';
import { Banner, Button } from '@shining-technologies/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import { useStopImpersonation } from '@/features/auth/impersonation.queries';

/** Set on <html> to the banner's height while it is shown; see below. */
const BANNER_HEIGHT_VAR = '--admin-banner-h';

/**
 * Loud, persistent impersonation banner. Dismissible ONLY by ending the session.
 * Tagged + audited server-side: the impersonation session is real (the `imp`
 * claim), every request is auditable, and "End impersonation" calls the real
 * `POST /api/admin/impersonate/stop` to restore the original admin.
 *
 * FIX(admin-impersonation-banner-source): driven by the server's `/me` —
 * `user` is the account being acted AS, `impersonator` the admin behind it.
 * Before: it read a persisted localStorage store, which survived logout,
 * disagreed with the cookie after a stop in another tab/app, and never showed
 * for an impersonation started elsewhere. After: it shows exactly when the
 * session cookie is an impersonation.
 *
 * FIX(admin-shell-banner-offset): the kit `AppShell` scrolls the document and
 * pins its header and sidebar at `top: 0`. Before (kit shell): a banner above
 * the shell would scroll away, or — made sticky — be covered by the header.
 * After: the banner sticks at the top and publishes its height as
 * `--admin-banner-h` on <html>; the header (`Topbar`) and the sidebar stick
 * below it, and the sidebar/shell heights subtract it. Unset (= 0px) whenever
 * no banner is shown.
 */
export function ImpersonationBanner() {
  const { user } = useAuth();
  const stopImpersonation = useStopImpersonation();
  const impersonator = user?.impersonator;
  const shown = Boolean(user && impersonator);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!shown || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [shown]);

  if (!user || !impersonator) return null;

  return (
    <Banner
      ref={ref}
      tone="warning"
      // An impersonated session must be announced, not just shown (the kit's
      // warning banner is a polite `status`).
      role="alert"
      icon={<UserCog aria-hidden />}
      className="sticky top-0 z-chrome"
      action={
        <Button
          size="sm"
          variant="outline"
          disabled={stopImpersonation.isPending}
          onClick={() => stopImpersonation.mutate()}
        >
          <LogOut aria-hidden /> End impersonation
        </Button>
      }
    >
      {/* FIX(admin-impersonation-copy): Before: "Impersonating X as Admin" read
          as if the admin were the one being impersonated. After: target first,
          the real actor in parentheses. */}
      Impersonating <strong>{user.name}</strong> (signed in as {impersonator.name}). All actions are recorded.
    </Banner>
  );
}
